import * as Yup from "yup";
import { InvoiceTarget, JaNej } from "../model/registrationEnums";

export const SELECT_REQUIRED_MESSAGE = "Valg er påkrævet.";

export const cvrField = Yup.string()
  .required("CVR-nummer er påkrævet.")
  .matches(/^\d{8}$/, "CVR-nummer skal være 8 cifre.");

export const pNumberField = Yup.string()
  .required("P-nummer er påkrævet.")
  .matches(/^\d{10}$/, "P-nummer skal være 10 cifre.");

export const phoneField = Yup.string()
  .required("Telefonnummer er påkrævet.")
  .matches(/^\d{8}$/, "Indtast et gyldigt dansk telefonnummer (8 cifre).");

export const phoneFieldOptional = Yup.string()
  .notRequired()
  .nullable()
  .test(
    "phone-format",
    "Indtast et gyldigt dansk telefonnummer (8 cifre).",
    (value) => !value || /^\d{8}$/.test(value),
  );

export const emailRequired = Yup.string()
  .email("Ugyldig e-mailadresse.")
  .required("E-mailadresse er påkrævet.");

export const emailOptional = Yup.string()
  .email("Ugyldig e-mailadresse.")
  .notRequired()
  .nullable();

export const jaNej = Yup.string()
  .required(SELECT_REQUIRED_MESSAGE)
  .oneOf([JaNej.Ja, JaNej.Nej], SELECT_REQUIRED_MESSAGE);

export const invoiceTarget = Yup.string()
  .required(SELECT_REQUIRED_MESSAGE)
  .oneOf(
    [
      InvoiceTarget.EBoks,
      InvoiceTarget.Mail,
      InvoiceTarget.Brev,
      InvoiceTarget.EAN,
    ],
    SELECT_REQUIRED_MESSAGE,
  );

/**
 * How invoices are sent, plus the one detail the chosen channel needs. Invoices
 * no longer follow the company e-mail: "Mail" requires its own address.
 */
export const invoiceDispatchFields = {
  invoiceTarget,
  invoiceEmail: Yup.string().when("invoiceTarget", {
    is: InvoiceTarget.Mail,
    then: (schema) =>
      schema
        .email("Ugyldig e-mailadresse.")
        .required("E-mailadresse til fakturaer er påkrævet."),
    otherwise: (schema) => schema.notRequired(),
  }),
  eanNumber: Yup.string().when("invoiceTarget", {
    is: InvoiceTarget.EAN,
    then: (schema) =>
      schema
        .required("EAN-nummer er påkrævet.")
        .matches(/^\d{13}$/, "EAN-nummer skal være 13 cifre."),
    otherwise: (schema) => schema.notRequired(),
  }),
};

export const invoiceDispatchSchema = Yup.object(invoiceDispatchFields);

export const requiredDate = Yup.string()
  .required("Dato er påkrævet.")
  .test(
    "iso-date",
    "Ugyldig dato.",
    (value) =>
      typeof value === "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(value) &&
      !Number.isNaN(Date.parse(value)),
  );

const employeeRow = (
  employeePhoneSchema:
    typeof phoneField | typeof phoneFieldOptional = phoneField,
) =>
  Yup.object({
    employeeName: Yup.string().required("Navn er påkrævet."),
    employeeRole: Yup.string().required("Stilling er påkrævet."),
    employeeEmail: Yup.string()
      .email("Ugyldig e-mailadresse.")
      .required("E-mailadresse er påkrævet."),
    employeePhone: employeePhoneSchema,
  });

/**
 * Bemærkninger og kontakt ved spørgsmål til registreringen (ADV-293, ændring
 * 11 og 12) — de tre felter findes på alle seks blanketter.
 *
 * Kontakttelefonen genbruger `phoneField`, altså samme 8-cifrede danske format
 * som blankettens øvrige telefonnumre. Afdelingen bad om "et obligatorisk
 * tal-felt", men feltet er et type="tel" i UI'et: et type="number" viser
 * spinner-pile og æder foranstillede nuller.
 */
export const remarksAndContactFields = {
  remarks: Yup.string().notRequired(),
  contactEmail: emailRequired,
  contactPhone: phoneField,
};

export const employeeListWhen = (
  employeesKey: string,
  employeePhoneSchema:
    typeof phoneField | typeof phoneFieldOptional = phoneField,
) =>
  Yup.array().when(employeesKey, {
    is: JaNej.Ja,
    then: () =>
      Yup.array()
        .of(employeeRow(employeePhoneSchema))
        .min(1, "Angiv mindst én ansat.")
        .required(),
    otherwise: () => Yup.array().notRequired(),
  });
